import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import SEO from "@/components/SEO";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { format } from "date-fns";
import { toast } from "sonner";
import { createQuote, trackSiteVisit } from "@/lib/api";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import GoldShieldLogo from "@/components/GoldShieldLogo";
import { 
  Truck, 
  ArrowLeft, 
  CalendarIcon, 
  Package, 
  FileText, 
  Loader2,
  CreditCard,
  Clock
} from "lucide-react";
import { cn } from "@/lib/utils";

// Form validation schema
const quoteSchema = z.object({
  company_name: z.string().min(1, "Company name is required"),
  contact_name: z.string().min(1, "Contact name is required"),
  email: z.string().email("Invalid email address"),
  phone: z.string().min(10, "Phone number must be at least 10 digits"),
  cargo_type: z.enum(["documents", "parcels", "pallet"]),
  pallet_count: z.number().min(1).optional().nullable(),
  total_weight_kg: z.number().min(0.1, "Weight must be greater than 0").max(1200, "Maximum weight is 1200kg"),
  dimensions: z.string().optional(),
  ready_time: z.date({ required_error: "Ready time is required" }),
  delivery_deadline: z.date({ required_error: "Delivery deadline is required" }),
  urgency: z.enum(["immediate", "same_day", "overnight"]),
  additional_notes: z.string().optional(),
  terms_accepted: z.literal(true, {
    errorMap: () => ({ message: "You must accept the Terms & Conditions and Privacy Policy." })
  }),
});

export default function GetQuotePage() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [readyDate, setReadyDate] = useState(null);
  const [deadlineDate, setDeadlineDate] = useState(null);
  const [readyTime, setReadyTime] = useState("09:00");
  const [deadlineTime, setDeadlineTime] = useState("17:00");
  
  // Address states
  const [pickupAddress, setPickupAddress] = useState(null);
  const [deliveryAddress, setDeliveryAddress] = useState(null);
  const [addressErrors, setAddressErrors] = useState({ pickup: null, delivery: null });

  // Track site visit on page load
  useEffect(() => {
    trackSiteVisit();
  }, []);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(quoteSchema),
    defaultValues: {
      cargo_type: "parcels",
      urgency: "same_day",
      pallet_count: null,
      terms_accepted: false,
    },
  });

  const cargoType = watch("cargo_type");

  // Validate address has required fields
  const validateAddress = (address, type) => {
    if (!address) return `${type} address is required`;
    if (!address.contact_name || address.contact_name.length < 1) return `${type} contact name is required`;
    if (!address.contact_phone || address.contact_phone.length < 10) return `${type} contact phone is required (min 10 digits)`;
    if (!address.address_line_1 || address.address_line_1.length < 3) return `${type} street address is required`;
    if (!address.city || address.city.length < 2) return `${type} city is required`;
    if (!address.postcode || address.postcode.length < 5) return `${type} postcode is required`;
    return null;
  };

  const onSubmit = async (data) => {
    // Validate addresses
    const pickupError = validateAddress(pickupAddress, "Pickup");
    const deliveryError = validateAddress(deliveryAddress, "Delivery");
    
    if (pickupError || deliveryError) {
      setAddressErrors({ pickup: pickupError, delivery: deliveryError });
      if (pickupError) toast.error(pickupError);
      else if (deliveryError) toast.error(deliveryError);
      return;
    }
    
    setAddressErrors({ pickup: null, delivery: null });
    setIsSubmitting(true);
    
    try {
      // Combine date and time
      const readyDateTime = new Date(readyDate);
      const [rh, rm] = readyTime.split(':');
      readyDateTime.setHours(parseInt(rh), parseInt(rm));

      const deadlineDateTime = new Date(deadlineDate);
      const [dh, dm] = deadlineTime.split(':');
      deadlineDateTime.setHours(parseInt(dh), parseInt(dm));

      const { terms_accepted, ...cleanData } = data;
      const submitData = {
        ...cleanData,
        ready_time: readyDateTime.toISOString(),
        delivery_deadline: deadlineDateTime.toISOString(),
        pallet_count: data.cargo_type === "pallet" ? data.pallet_count : null,
        // New structured addresses
        pickup_address: pickupAddress,
        delivery_address: deliveryAddress,
        // Legacy postcodes for backward compatibility
        pickup_postcode: pickupAddress?.postcode || "",
        delivery_postcode: deliveryAddress?.postcode || "",
        terms_accepted_at: new Date().toISOString(),
      };

      const response = await createQuote(submitData);
      
      // Check if we got a valid response with success=true and ID
      if (response && response.success === true && response.id) {
        // Track form submission in Google Tag
        if (window.trackFormSubmission) {
          window.trackFormSubmission();
        }
        toast.success("Quote request submitted successfully!");
        navigate(`/quote-submitted/${response.id}`);
      } else if (response && response.id) {
        // Fallback - if we have ID but no success flag, still proceed
        // Track form submission in Google Tag
        if (window.trackFormSubmission) {
          window.trackFormSubmission();
        }
        toast.success("Quote request submitted!");
        navigate(`/quote-submitted/${response.id}`);
      } else {
        // Unexpected response format
        toast.error("Unexpected response. Please contact support.");
      }
    } catch (error) {
      console.error("Quote submission error:", error);
      // Only show error if it's actually a failed request
      const errorMessage = error.response?.data?.detail || error.message || "Failed to submit quote request";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0F14]">
      <SEO 
        title="Get a Quote | RTS EXPRESS - Same Day Courier UK"
        description="Request a competitive quote for urgent same-day delivery across the UK. Dedicated 3.5T van transport, instant pricing, secure online payment."
        path="/get-quote"
      />
      {/* Header - Premium Glass Effect */}
      <header className="sticky top-0 z-50 bg-[#0B0F14]/95 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <GoldShieldLogo className="w-10 h-12" />
            <div className="flex flex-col">
              <span className="font-bold text-white text-lg tracking-wide leading-tight">
                RTS EXPRESS
              </span>
              <span className="text-[10px] text-slate-400 tracking-[0.2em] uppercase">Driven by Delivery</span>
            </div>
          </Link>
          <Link to="/">
            <Button variant="ghost" className="text-slate-400 hover:text-white hover:bg-white/5">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </Link>
        </div>
      </header>

      {/* Form */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-10">
          <span className="text-[#22c55e] text-sm font-semibold tracking-[0.2em] uppercase mb-3 block">Request a Quote</span>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3">
            Get Your <span className="text-[#22c55e]">Quote</span>
          </h1>
          <p className="text-slate-400 text-lg">
            Complete the form below and we'll send you a competitive quote.
          </p>
          {/* Support Phone Number */}
          <div className="mt-4 flex items-center gap-2 text-slate-300">
            <svg className="w-5 h-5 text-[#22c55e]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
            <span className="text-sm">Need help? Call us at <a href="tel:+442477102568" className="text-[#22c55e] font-semibold hover:underline">+44 2477 102 568</a> if you have difficulties completing the form.</span>
          </div>
        </div>

        {/* Payment Notice - Premium Style */}
        <div className="mb-5 p-5 bg-amber-500/10 border border-amber-500/20 rounded-xl">
          <p className="text-amber-400 text-sm flex items-center gap-3">
            <CreditCard className="w-5 h-5 flex-shrink-0" />
            <span><strong>Payment secured before dispatch</strong> — Your delivery starts the moment payment clears</span>
          </p>
        </div>

        {/* Address Accuracy Notice - Premium Style */}
        <div className="mb-10 p-6 bg-white/5 border border-white/10 rounded-xl">
          <p className="text-[#60a5fa] text-sm font-semibold mb-3 flex items-center gap-2">
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Important – Please verify your address details
          </p>
          <p className="text-slate-300 text-sm mb-3">
            To ensure a smooth and on-time collection and delivery, please enter the <strong className="text-white">COMPLETE and ACCURATE</strong> address:
          </p>
          <ul className="text-slate-400 text-sm space-y-1.5 ml-5 list-disc">
            <li>House or unit number and street name</li>
            <li>Company name and unit number (if applicable)</li>
            <li>City/Town</li>
            <li>Correct postcode</li>
          </ul>
          <p className="text-slate-400 text-sm mt-4">
            Incorrect or incomplete address details may cause delays or require re-routing, which can result in <strong className="text-amber-400">additional charges</strong>.
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          {/* Contact Details */}
          <div className="bg-white/5 border border-white/10 rounded-xl p-6 lg:p-8">
            <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-3">
              <span className="w-10 h-10 bg-[#22c55e]/10 border border-[#22c55e]/30 rounded-lg flex items-center justify-center text-[#22c55e] text-sm font-bold">1</span>
              Your Contact Details
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-medium">Company Name (Required) *</Label>
                <Input
                  {...register("company_name")}
                  className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:ring-[#22c55e] focus:border-[#22c55e] rounded-lg h-12"
                  placeholder="Your company name"
                  data-testid="company-name-input"
                />
                {errors.company_name && (
                  <p className="text-red-400 text-xs">{errors.company_name.message}</p>
                )}
                <p className="text-slate-500 text-xs mt-1">
                  This service is available to registered business entities only. By submitting this form, you confirm that you are acting on behalf of a company.
                </p>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-medium">Contact Name *</Label>
                <Input
                  {...register("contact_name")}
                  className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:ring-[#22c55e] focus:border-[#22c55e] rounded-lg h-12"
                  placeholder="Your name"
                  data-testid="contact-name-input"
                />
                {errors.contact_name && (
                  <p className="text-red-400 text-xs">{errors.contact_name.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-medium">Email *</Label>
                <Input
                  {...register("email")}
                  type="email"
                  className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:ring-[#22c55e] focus:border-[#22c55e] rounded-lg h-12"
                  placeholder="email@company.com"
                  data-testid="email-input"
                />
                {errors.email && (
                  <p className="text-red-400 text-xs">{errors.email.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-medium">Phone *</Label>
                <Input
                  {...register("phone")}
                  type="tel"
                  className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:ring-[#22c55e] focus:border-[#22c55e] rounded-lg h-12"
                  placeholder="+44 7XXX XXXXXX"
                  data-testid="phone-input"
                />
                {errors.phone && (
                  <p className="text-red-400 text-xs">{errors.phone.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Pickup Address */}
          <div className="bg-white/5 border border-white/10 rounded-xl p-6 lg:p-8">
            <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-3">
              <span className="w-10 h-10 bg-[#22c55e]/10 border border-[#22c55e]/30 rounded-lg flex items-center justify-center text-[#22c55e] text-sm font-bold">2</span>
              Pickup Address
            </h2>
            <AddressAutocomplete
              prefix="pickup"
              label="Collection Point"
              onAddressChange={setPickupAddress}
            />
            {addressErrors.pickup && (
              <p className="text-red-400 text-sm mt-4">{addressErrors.pickup}</p>
            )}
          </div>

          {/* Delivery Address */}
          <div className="bg-white/5 border border-white/10 rounded-xl p-6 lg:p-8">
            <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-3">
              <span className="w-10 h-10 bg-[#3b82f6]/10 border border-[#3b82f6]/30 rounded-lg flex items-center justify-center text-[#3b82f6] text-sm font-bold">3</span>
              Delivery Address
            </h2>
            <AddressAutocomplete
              prefix="delivery"
              label="Delivery Point"
              onAddressChange={setDeliveryAddress}
            />
            {addressErrors.delivery && (
              <p className="text-red-400 text-sm mt-4">{addressErrors.delivery}</p>
            )}
          </div>

          {/* Cargo Details */}
          <div className="bg-white/5 border border-white/10 rounded-xl p-6 lg:p-8">
            <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-3">
              <span className="w-10 h-10 bg-[#22c55e]/10 border border-[#22c55e]/30 rounded-lg flex items-center justify-center text-[#22c55e] text-sm font-bold">4</span>
              Cargo Details
            </h2>
            
            {/* Cargo Type Selection */}
            <div className="mb-6">
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-4 block">Cargo Type *</Label>
              <div className="grid grid-cols-3 gap-4">
                {[
                  { value: "documents", label: "Documents", icon: FileText, color: "#22c55e" },
                  { value: "parcels", label: "Parcels", icon: Package, color: "#3b82f6" },
                  { value: "pallet", label: "Pallets", icon: Truck, color: "#f59e0b" },
                ].map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setValue("cargo_type", type.value)}
                    className={cn(
                      "p-5 rounded-xl border transition-all duration-300",
                      cargoType === type.value
                        ? "bg-white/10 border-[#22c55e]/50"
                        : "bg-white/5 border-white/10 hover:border-white/20 hover:bg-white/[0.07]"
                    )}
                    data-testid={`cargo-type-${type.value}`}
                  >
                    <type.icon className={cn(
                      "w-7 h-7 mx-auto mb-3 transition-colors",
                      cargoType === type.value ? `text-[${type.color}]` : "text-slate-500"
                    )} style={cargoType === type.value ? { color: type.color } : {}} />
                    <span className={cn(
                      "text-sm font-medium block",
                      cargoType === type.value ? "text-white" : "text-slate-400"
                    )}>{type.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Pallet Count (conditional) */}
            {cargoType === "pallet" && (
              <div className="mb-6 p-5 bg-white/5 border border-white/10 rounded-xl">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-3 block">Number of Pallets *</Label>
                <Input
                  {...register("pallet_count", { valueAsNumber: true })}
                  type="number"
                  min="1"
                  placeholder="Enter number of pallets"
                  className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:ring-[#22c55e] focus:border-[#22c55e] rounded-lg h-12 w-40"
                  data-testid="pallet-count-input"
                />
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-medium">Total Weight (kg) *</Label>
                <Input
                  {...register("total_weight_kg", { valueAsNumber: true })}
                  type="number"
                  step="0.1"
                  className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:ring-[#22c55e] focus:border-[#22c55e] rounded-lg h-12"
                  placeholder="e.g. 150"
                  data-testid="weight-input"
                />
                {errors.total_weight_kg && (
                  <p className="text-red-400 text-xs">{errors.total_weight_kg.message}</p>
                )}
                <p className="text-slate-500 text-xs">Maximum: 1200kg (UK legal limit)</p>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-medium">Dimensions (optional)</Label>
                <Input
                  {...register("dimensions")}
                  className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:ring-[#22c55e] focus:border-[#22c55e] rounded-lg h-12"
                  placeholder="e.g. 120x80x100 cm"
                  data-testid="dimensions-input"
                />
              </div>
            </div>
          </div>

          {/* Timing */}
          <div className="bg-white/5 border border-white/10 rounded-xl p-6 lg:p-8">
            <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-3">
              <span className="w-10 h-10 bg-[#22c55e]/10 border border-[#22c55e]/30 rounded-lg flex items-center justify-center text-[#22c55e] text-sm font-bold">5</span>
              Timing
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              {/* Ready Time */}
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-medium">Ready for Collection *</Label>
                <div className="flex gap-3">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "flex-1 justify-start text-left font-normal bg-white/5 border-white/10 text-white hover:bg-white/10 rounded-lg h-12",
                          !readyDate && "text-slate-500"
                        )}
                        data-testid="ready-date-btn"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {readyDate ? format(readyDate, "PPP") : "Select date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-[#111] border-white/10 rounded-xl" align="start">
                      <Calendar
                        mode="single"
                        selected={readyDate}
                        onSelect={(date) => {
                          setReadyDate(date);
                          setValue("ready_time", date);
                        }}
                        disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                        className="bg-[#111] text-white rounded-xl"
                      />
                    </PopoverContent>
                  </Popover>
                  <Input
                    type="time"
                    value={readyTime}
                    onChange={(e) => setReadyTime(e.target.value)}
                    className="w-32 bg-white/5 border-white/10 text-white focus:ring-[#22c55e] focus:border-[#22c55e] rounded-lg h-12"
                    data-testid="ready-time-input"
                  />
                </div>
                {errors.ready_time && (
                  <p className="text-red-400 text-xs">{errors.ready_time.message}</p>
                )}
              </div>

              {/* Delivery Deadline */}
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-medium">Delivery Deadline *</Label>
                <div className="flex gap-3">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "flex-1 justify-start text-left font-normal bg-white/5 border-white/10 text-white hover:bg-white/10 rounded-lg h-12",
                          !deadlineDate && "text-slate-500"
                        )}
                        data-testid="deadline-date-btn"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {deadlineDate ? format(deadlineDate, "PPP") : "Select date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-[#111] border-white/10 rounded-xl" align="start">
                      <Calendar
                        mode="single"
                        selected={deadlineDate}
                        onSelect={(date) => {
                          setDeadlineDate(date);
                          setValue("delivery_deadline", date);
                        }}
                        disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                        className="bg-[#111] text-white rounded-xl"
                      />
                    </PopoverContent>
                  </Popover>
                  <Input
                    type="time"
                    value={deadlineTime}
                    onChange={(e) => setDeadlineTime(e.target.value)}
                    className="w-32 bg-white/5 border-white/10 text-white focus:ring-[#22c55e] focus:border-[#22c55e] rounded-lg h-12"
                    data-testid="deadline-time-input"
                  />
                </div>
                {errors.delivery_deadline && (
                  <p className="text-red-400 text-xs">{errors.delivery_deadline.message}</p>
                )}
              </div>
            </div>

            {/* Service Selection */}
            <div className="space-y-4">
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-medium">Service Type *</Label>
              <Select
                defaultValue="same_day"
                onValueChange={(value) => setValue("urgency", value)}
              >
                <SelectTrigger 
                  className="bg-white/5 border-white/10 text-white rounded-lg h-12"
                  data-testid="urgency-select"
                >
                  <SelectValue placeholder="Select service type" />
                </SelectTrigger>
                <SelectContent className="bg-[#111] border-white/10 rounded-xl">
                  <SelectItem value="same_day" className="text-white hover:bg-white/10 rounded-lg py-3">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2 font-semibold">
                        <Clock className="w-4 h-4 text-[#f59e0b]" />
                        EXECUTIVE DEDICATED SAME-DAY
                      </div>
                      <span className="text-xs text-slate-400 ml-6">Direct vehicle allocation. Exclusive transport.</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="overnight" className="text-white hover:bg-white/10 rounded-lg py-3">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2 font-semibold">
                        <Clock className="w-4 h-4 text-[#3b82f6]" />
                        PRIORITY COMMERCIAL DEPLOYMENT
                      </div>
                      <span className="text-xs text-slate-400 ml-6">Accelerated mobilisation for time-sensitive consignments.</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="immediate" className="text-white hover:bg-white/10 rounded-lg py-3">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2 font-semibold">
                        <Clock className="w-4 h-4 text-red-400" />
                        CRITICAL EXECUTIVE RESPONSE
                      </div>
                      <span className="text-xs text-slate-400 ml-6">Immediate deployment for mission-critical consignments.</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-2">All services operate on a dedicated vehicle basis. Mobilisation times subject to operational conditions.</p>
            </div>
          </div>

          {/* Additional Notes */}
          <div className="bg-white/5 border border-white/10 rounded-xl p-6 lg:p-8">
            <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-3">
              <span className="w-10 h-10 bg-[#22c55e]/10 border border-[#22c55e]/30 rounded-lg flex items-center justify-center text-[#22c55e] text-sm font-bold">6</span>
              Additional Notes
            </h2>
            <Textarea
              {...register("additional_notes")}
              className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:ring-[#22c55e] focus:border-[#22c55e] min-h-[120px] rounded-xl"
              placeholder="Any special instructions, access requirements, or additional information..."
              data-testid="notes-input"
            />
          </div>

        <div className="bg-white/5 border border-white/10 rounded-xl p-6 lg:p-8" data-testid="terms-acceptance-section">
          <label className="flex items-start gap-4 text-sm text-slate-300 cursor-pointer">
            <input
              type="checkbox"
              className="mt-1 h-5 w-5 rounded-md border-white/20 bg-white/5 text-[#22c55e] focus:ring-[#22c55e] cursor-pointer"
              data-testid="terms-acceptance-checkbox"
              {...register("terms_accepted")}
            />
            <span>
              I confirm that I have read and agree to the{' '}
              <Link to="/terms-and-conditions" className="text-[#22c55e] hover:underline font-medium" data-testid="terms-link">
                Terms &amp; Conditions
              </Link>
              {' '}and{' '}
              <Link to="/privacy-policy" className="text-[#22c55e] hover:underline font-medium" data-testid="privacy-link">
                Privacy Policy
              </Link>.
            </span>
          </label>
          {errors.terms_accepted && (
            <p className="text-sm text-red-400 mt-3" data-testid="terms-acceptance-error">
              {errors.terms_accepted.message}
            </p>
          )}
        </div>

          {/* Submit Button */}
          <div className="flex flex-col sm:flex-row gap-4 justify-end pt-4">
            <Link to="/">
              <Button 
                type="button" 
                variant="outline" 
                className="w-full sm:w-auto border-white/10 text-slate-400 hover:bg-white/5 hover:text-white rounded-lg h-12 px-8"
              >
                Cancel
              </Button>
            </Link>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full sm:w-auto bg-[#22c55e] text-black hover:bg-[#1ea550] font-bold px-10 rounded-lg h-12 border-0"
              data-testid="submit-quote-btn"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Submitting...
                </>
              ) : (
                "Submit Quote Request"
              )}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
